(ns app.front.entity-edit
  "A helper for doing update-x type process actions
  that take {:id .. :attr .. :value ..} as parameters.

  Values not-yet-saved are stored in app-db, and override values
  in entity-db.

  You can either bind events for immediate debounced saving like such:

  {:on-change #(rf/dispatch-sync [::ee/save-debounce id attr v])}

  or save with on-blur:

    {:on-change #(rf/dispatch-sync [::ee/set-value id attr v])
     :on-blur   #(>evt [::ee/save
                        app-or-exe
                        :process.signal/update-something
                        id
                        attr
                        v])}

  Preferably use rf/dispatch-sync for controlled text fields, and
  >evt for everything else.

  Or you can accumulate changes and save them all at once, by using
  just the `::set-value` event and save at once by dispatching:

    (>evt [::save-changes app-or-exe :process.signal/update-something id])"
  (:require [app.front.entities :as entities]
            [clojure.string :as str]
            [core.front.event :refer [>evt]]
            [potpuri.core :as p]
            [re-frame.core :as rfc]
            [re-frame.core-instrumented :as rf]))

;;; Internals
;;;;;;;;;;;;;

(defn- save-signal
  ([process-target signal-or-command entity-id attr value signal-opts]
   (save-signal process-target signal-or-command entity-id attr value signal-opts false))
  ([process-target signal-or-command entity-id-or-ids attr value signal-opts multi?]
   (let [id-keyword  (or (:id-keyword signal-opts) (if multi? :ids :id))
         signal-opts (dissoc signal-opts :id-keyword)
         data        (-> {:attr  attr
                          :value value}
                         (p/assoc-if id-keyword entity-id-or-ids)
                         (merge (:additional-data signal-opts)))
         signal?     (some? process-target)
         on-done     (if signal?
                       (if multi? ::multi-save-done ::save-done)
                       (if multi? ::multi-save-done-command ::save-done-command))
         opts        (merge signal-opts {:on-done [on-done entity-id-or-ids attr value]})]
     (if signal?
       [:app.front.process/signal process-target [signal-or-command data] opts]
       [::command (merge {:command        signal-or-command
                          :data           data
                          :on-bad-request [:comm/add-error-notification]}
                         opts)]))))

(defn- save-multiple-with-signal [process-target signal-or-command entity-ids attr value signal-opts]
  (save-signal process-target signal-or-command entity-ids attr value signal-opts true))

(defn- save-signals [process-target signal-or-command db entity-id signal-opts]
  (for [[attr value] (-> db ::unsaved (get entity-id))]
    (save-signal process-target signal-or-command entity-id attr value signal-opts)))


(defn set-value [db entity-id attr value]
  (assoc-in db [::unsaved entity-id attr] value))


(defn set-values [db entity-id value-map]
  (assoc-in db [::unsaved entity-id] value-map))


(rf/reg-event-db ::save-done
  (fn [db [_ entity-id attr value status _]]
    (when (= 200 status)
      (-> db
          ;; clear local changes if they match with what
          ;; was successfully persisted
          (update-in [::unsaved entity-id]
                     (fn [pending]
                       (if (= value (attr pending))
                         (dissoc pending attr)
                         pending)))
          ;; clear the whole unsaved map if it is empty now
          (update ::unsaved
                  (fn [unsaved]
                    (cond-> unsaved
                      (empty? (get unsaved entity-id))
                      (dissoc entity-id))))))))


(rf/reg-event-fx ::multi-save-done
  (fn [_ [_ entity-ids attr value status _]]
    {:fx (for [entity-id entity-ids]
           [:dispatch [::save-done entity-id attr value status]])}))


; Command requires explicit ::entities/apply-changes
(rf/reg-event-fx ::save-done-command
  (fn [_ [_ _ _ _ status resp :as params]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  (into [::save-done] (rest params))]}))


(rf/reg-event-fx ::multi-save-done-command
  (fn [_ [_ entity-ids attr value status resp]]
    {:fx (concat [[:dispatch [::entities/apply-changes status resp]]]
                 (for [entity-id entity-ids]
                   [:dispatch [::save-done entity-id attr value status]]))}))


;;; Events
;;;;;;;;;;

;; set value locally
(rf/reg-event-db ::set-value
  (fn [db [_ entity-id attr value]]
    (set-value db entity-id attr value)))


;; set multiple values locally at once
(rf/reg-event-db ::set-values
  (fn [db [_ entity-id value-map]]
    (set-values db entity-id value-map)))


(rf/reg-event-fx ::command
  (fn [_ [_ params]]
    {:comm/command params}))


;; save value to server (and set it locally while waiting for result)
(rf/reg-event-fx ::save
  (fn [_ [_ process-target signal-or-command entity-id attr value signal-opts]]
    {:dispatch-debounce [{:id     (str/join "." [process-target signal-or-command entity-id attr])
                          :action :cancel}]
     :dispatch-n        [(save-signal process-target signal-or-command entity-id attr value signal-opts)
                         [::set-value entity-id attr value]]}))


(rf/reg-event-fx ::multi-save
  (fn [_ [_ process-target signal-or-command entity-ids attr value signal-opts]]
    {:fx (concat [[:dispatch-debounce (for [entity-id entity-ids]
                                        {:id     (str/join "." [process-target signal-or-command entity-id attr])
                                         :action :cancel})]
                  [:dispatch (save-multiple-with-signal process-target signal-or-command entity-ids attr value signal-opts)]]
                 (for [entity-id entity-ids]
                   [:dispatch [::set-value entity-id attr value]]))}))


(rf/reg-event-fx ::save-debounce
  (fn [{:keys [db]} [_ process-target signal-or-command entity-id attr value signal-opts]]
    {:dispatch-debounce [{:id      (str/join "." [process-target signal-or-command entity-id attr])
                          :timeout 500
                          :action  :dispatch
                          :event   (save-signal process-target signal-or-command entity-id attr value signal-opts)}]
     :db                (set-value db (or entity-id process-target) attr value)}))


;; save all local changes at once
(rf/reg-event-fx ::save-changes
  (fn [{:keys [db]} [_ process-target signal-id entity-id signal-opts]]
    {:dispatch-n (save-signals process-target signal-id db entity-id signal-opts)}))


;; cancel all local changes at once
(rf/reg-event-db ::cancel-changes
  (fn [db [_ entity-id]]
    (p/dissoc-in db [::unsaved entity-id])))


;;; Event factory functions
;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn- save-fn-internal
  ([dispatch-with event signal-id]
   (save-fn-internal dispatch-with event signal-id {}))
  ([dispatch-with event signal-id signal-opts]
   (fn
     ([process-target attr value]
      (dispatch-with [event process-target signal-id nil attr value signal-opts]))
     ([process-target entity-id attr value]
      (dispatch-with [event process-target signal-id entity-id attr value signal-opts])))))

(def save-fn
  "Given a `signal-id`, make a function that takes
  `process-target`, `entity-id`, `attr` and `value` and saves
  that value immediately to the server"
  (partial save-fn-internal rfc/dispatch ::save))

(def multi-save-fn
  "Given a `signal-id`, make a function that takes
  `process-target`, list of `entity-id`s, `attr` and `value` and saves
  that value immediately to the server"
  (partial save-fn-internal rfc/dispatch ::multi-save))

(def save-debounce-fn
  "Given a `signal-id`, make a function that takes
  `process-target`, `entity-id`, `attr` and `value` and saves
  that value after a debounce period to the server"
  (partial save-fn-internal rfc/dispatch-sync ::save-debounce))


(defn save-changes-fn
  "Give a `signal-id` keyword, make a function that takes
  `process-target` and `entity-id` and saves all local changes
  to the server."
  [signal-id]
  (fn
    ([process-target entity-id]
     (>evt [::save-changes
            process-target
            signal-id
            entity-id
            {}]))
    ([process-target entity-id signal-opts]
     (>evt [::save-changes
            process-target
            signal-id
            entity-id
            signal-opts]))))


;;; Subscriptions
;;;;;;;;;;;;;;;;;
(rf/reg-sub ::unsaved
  :-> ::unsaved)

(rf/reg-sub ::has-any-unsaved?
  :<- [::unsaved]
  (fn [edits _]
    (not (empty? edits))))

(rf/reg-sub ::unsaved-for
  :<- [::unsaved]
  (fn [unsaved [_ entity-id]] (get unsaved entity-id)))

(rf/reg-sub ::has-unsaved-for-any?
  :<- [::unsaved]
  (fn [unsaved [_ attrs]]
    (->> unsaved
         (some (fn [[_ attr-map]]
                 (some (set attrs) (keys attr-map))))
         boolean)))

(rf/reg-sub ::has-changes?
  :<- [::unsaved]
  (fn [unsaved [_ entity-id]]
    (-> unsaved
        (get entity-id)
        seq
        boolean)))

(defn contains-path? [coll path]
  (if (-> path count (= 1))
    (contains? coll (first path))
    (recur (get coll (first path)) (rest path))))

(rf/reg-sub ::attr-value
  :<- [::entities/entity-db]
  :<- [::unsaved]
  (fn [[entity-db unsaved] [_ entity-id attr]]
    (let [path [entity-id attr]]
      (if (contains-path? unsaved path)
        (get-in unsaved path)
        (get-in entity-db path)))))
